// hooks/useLanguage.js - Custom hook for better language management
import { useTranslation } from "react-i18next";
import { useEffect, useCallback } from "react";

const useLanguage = () => {
  const { i18n, t } = useTranslation();

  // Handle direction and localStorage
  const setLanguageAndDirection = useCallback((lng: string) => {
    const isRTL = lng === "ar";

    // Set document attributes
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = lng;

    // Manage body classes
    if (isRTL) {
      document.body.classList.add("rtl");
      document.body.classList.remove("ltr");
    } else {
      document.body.classList.add("ltr");
      document.body.classList.remove("rtl");
    }

    // Save to localStorage
    try {
      localStorage.setItem("i18nextLng", lng);
    } catch (error) {
      console.warn("Failed to save language:", error);
    }
  }, []);

  // Toggle between languages
  const toggleLanguage = useCallback(() => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
    setLanguageAndDirection(newLang);
  }, [i18n, setLanguageAndDirection]);

  // Change to specific language
  const changeLanguage = useCallback(
    (lng: string) => {
      if (lng === "en" || lng === "ar") {
        i18n.changeLanguage(lng);
        setLanguageAndDirection(lng);
      }
    },
    [i18n, setLanguageAndDirection]
  );

  // Get alternative language info
  const getAlternativeLanguage = useCallback(() => {
    return i18n.language === "en" ? "AR" : "EN";
  }, [i18n.language]);

  const getAlternativeLanguageLabel = useCallback(() => {
    return i18n.language === "en" ? "العربية" : "English";
  }, [i18n.language]);

  const getCurrentLanguageLabel = useCallback(() => {
    return i18n.language === "en" ? "English" : "العربية";
  }, [i18n.language]);

  // Check if current language is RTL
  const isRTL = i18n.language === "ar";

  // Ensure direction is set on component mount
  useEffect(() => {
    setLanguageAndDirection(i18n.language);
  }, [i18n.language, setLanguageAndDirection]);

  return {
    // Core i18n
    t,
    i18n,

    // Current language info
    currentLanguage: i18n.language,
    isRTL,

    // Language switching
    toggleLanguage,
    changeLanguage,

    // Alternative language info (for UI display)
    alternativeLanguage: getAlternativeLanguage(),
    alternativeLanguageLabel: getAlternativeLanguageLabel(),
    currentLanguageLabel: getCurrentLanguageLabel(),

    // Utilities
    setLanguageAndDirection,
  };
};

export default useLanguage;
